import React from 'react';
import { Link } from 'react-router-dom';
import { AdminBookingDetailDto, AdminRidePersonDto } from '../api/client';
import { useI18n } from '../i18n/I18nProvider';
import { StatusBadge } from './ui';

export function bookingPersonName(person?: AdminRidePersonDto | null): string {
  return [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim() || person?.email || person?.id || '—';
}
export function formatBookingTime(value?: string | null, language = 'de'): string {
  return value ? new Intl.DateTimeFormat(language === 'de' ? 'de-AT' : 'en-GB', { timeZone: 'Europe/Vienna', dateStyle: 'medium', timeStyle: 'medium' }).format(new Date(value)) : '—';
}
export function BookingTimeline({ detail }: { detail: AdminBookingDetailDto }): React.ReactElement {
  const { t, language } = useI18n();
  return <section aria-label={t('bookings.timeline')}>
    <h2 className="mb-4 font-semibold text-gray-900">{t('bookings.timeline')}</h2>
    {detail.events.length === 0 && <p className="text-sm text-gray-500">{t('bookings.noEvents')}</p>}
    <ol className="ml-2 border-l border-gray-200">
      {detail.events.map(event => <li key={event.id} className="relative mb-5 pl-6">
        <span className="absolute -left-1.5 top-1 h-3 w-3 rounded-full border-2 border-white bg-gray-900" />
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-gray-900">{t(event.action === 'BOOKING_CREATED' && event.source === 'RIDE_OFFER' ? 'bookings.offerCreated' : `bookings.action.${event.action}`)}</span>
          <time className="text-xs text-gray-500" dateTime={event.occurredAt}>{formatBookingTime(event.occurredAt, language)}</time>
          {event.historical && <StatusBadge>{t('bookings.historical')}</StatusBadge>}
          {event.effect === 'BACKFILLED' && <StatusBadge tone="amber">{t('bookings.effect.BACKFILLED')}</StatusBadge>}
          {event.effect === 'DERIVED' && <StatusBadge tone="blue">{t('bookings.effect.DERIVED')}</StatusBadge>}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
          {event.actorRole === 'SYSTEM' ? <span>{t('bookings.role.SYSTEM')}</span>
            : event.actor ? <Link className="text-gray-900 hover:underline" to={`/users/${encodeURIComponent(event.actor.id)}`}>{bookingPersonName(event.actor)}</Link>
            : <span className="text-gray-500">{t('bookings.unknownActor')}</span>}
          {event.actorRole !== 'UNKNOWN' && event.actorRole !== 'SYSTEM' && <StatusBadge>{t(`bookings.role.${event.actorRole}`)}</StatusBadge>}
          {!event.actor && event.actorUserId && <span className="font-mono text-xs text-gray-500">{event.actorUserId}</span>}
        </div>
        <p className="mt-1 text-xs text-gray-500">{t(`bookings.source.${event.source}`)}{event.reason !== 'NONE' && <> · <span>{t(`bookings.reason.${event.reason}`)}</span></>}</p>
      </li>)}
    </ol>
  </section>;
}
